import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import {
  ChartBar,
  TrendUp,
  TrendDown,
  Buildings,
  Folder,
  CurrencyDollar,
  Target,
  Handshake,
  DownloadSimple,
  ArrowClockwise,
  Funnel,
  CheckSquare,
  FileText,
  PaperPlaneTilt,
  FilePdf,
  EnvelopeSimple,
  Timer,
  Play,
  Pause,
  ClockCounterClockwise, ChartLine} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line, FunnelChart, Funnel as ReFunnel,
  LabelList, Legend, Area, AreaChart
} from 'recharts';
import {
  getReportingKpi,
  getDossiersEvolution,
  getContributionsTerritoire,
  getDossiersType,
  getPipelineFunnel,
  getChecklistGlobal,
  exportCsv,
  previewMonthlyReport,
  sendMonthlyReport,
  getSchedulerStatus,
  updateSchedulerConfig,
  getSchedulerLogs
} from '../services/api';
import { toast } from 'sonner';
import ReportingExtras from '../components/ReportingExtras';
import { PageHero } from '../components/motion/PageHero';

const COLORS = ['#2D5A4C', '#D47A62', '#8DB0A1', '#E8A365', '#62A8D4', '#5E6D66', '#7B68A8', '#4ECDC4'];
const TERRITORY_COLORS = {
  'Guadeloupe': '#2D5A4C',
  'Martinique': '#D47A62',
  'Guyane': '#8DB0A1',
  'Reunion': '#E8A365',
  'Mayotte': '#62A8D4',
  'Saint-Martin': '#7B68A8',
  'Hexagone': '#5E6D66',
};

function KpiCard({ icon: Icon, label, value, sub, trend, color = '#2D5A4C' }) {
  return (
    <Card className="border-[#E8E6E1] hover:shadow-md transition-shadow" data-testid={`kpi-${label.toLowerCase().replace(/\s+/g, '-')}`}>
      <CardContent className="pt-5 pb-4 px-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <p className="text-xs font-medium text-[#5E6D66] uppercase tracking-wider">{label}</p>
            <p className="text-2xl font-bold text-[#1A2421] mt-1" style={{ fontFamily: 'Manrope' }}>{value}</p>
            {sub && <p className="text-xs text-[#5E6D66] mt-1">{sub}</p>}
          </div>
          <div className="w-10 h-10 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
            <Icon size={22} style={{ color }} weight="duotone" />
          </div>
        </div>
        {trend !== undefined && (
          <div className={`flex items-center gap-1 mt-2 text-xs font-medium ${trend >= 0 ? 'text-emerald-600' : 'text-red-500'}`}>
            {trend >= 0 ? <TrendUp size={14} /> : <TrendDown size={14} />}
            <span>{trend >= 0 ? '+' : ''}{trend}%</span>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border border-[#E8E6E1] rounded-lg p-3 shadow-lg">
      <p className="text-sm font-medium text-[#1A2421]">{label}</p>
      {payload.map((entry, i) => (
        <p key={i} className="text-xs text-[#5E6D66] mt-1">
          <span className="inline-block w-2 h-2 rounded-full mr-1.5" style={{ backgroundColor: entry.color }} />
          {entry.name}: <span className="font-semibold text-[#1A2421]">{typeof entry.value === 'number' && entry.value >= 1000 ? entry.value.toLocaleString('fr-FR') : entry.value}</span>
        </p>
      ))}
    </div>
  );
};

export function ReportingPage() {
  const [loading, setLoading] = useState(true);
  const [kpi, setKpi] = useState(null);
  const [evolution, setEvolution] = useState([]);
  const [contributions, setContributions] = useState([]);
  const [dossierTypes, setDossierTypes] = useState([]);
  const [pipeline, setPipeline] = useState([]);
  const [checklist, setChecklist] = useState(null);
  const [months, setMonths] = useState('12');
  const [exporting, setExporting] = useState(null);
  const [sendingReport, setSendingReport] = useState(false);
  const [downloadingReport, setDownloadingReport] = useState(false);
  const [schedulerStatus, setSchedulerStatus] = useState(null);
  const [togglingJob, setTogglingJob] = useState(null);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    try {
      const [kpiRes, evoRes, contribRes, typeRes, pipeRes, checkRes] = await Promise.all([
        getReportingKpi(),
        getDossiersEvolution(parseInt(months)),
        getContributionsTerritoire(),
        getDossiersType(),
        getPipelineFunnel(),
        getChecklistGlobal(),
      ]);
      setKpi(kpiRes.data);
      setEvolution(evoRes.data.data);
      setContributions(contribRes.data.data);
      setDossierTypes(typeRes.data.data);
      setPipeline(pipeRes.data.data);
      setChecklist(checkRes.data);
    } catch (err) {
      console.error('Reporting fetch error:', err);
      toast.error('Erreur lors du chargement des rapports');
    } finally {
      setLoading(false);
    }
  }, [months]);

  const fetchScheduler = useCallback(async () => {
    try {
      const res = await getSchedulerStatus();
      setSchedulerStatus(res.data);
    } catch {}
  }, []);

  useEffect(() => {
    fetchAll();
    fetchScheduler();
  }, [fetchAll, fetchScheduler]);

  const handleExport = async (entity) => {
    setExporting(entity);
    try {
      const res = await exportCsv(entity);
      const blob = new Blob([res.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `export_${entity}_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${entity} telecharge`);
    } catch (err) {
      toast.error('Erreur lors de l\'export');
    } finally {
      setExporting(null);
    }
  };

  const handlePreviewReport = async () => {
    setDownloadingReport(true);
    try {
      const res = await previewMonthlyReport();
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      const now = new Date();
      a.download = `rapport_mensuel_${now.getFullYear()}_${String(now.getMonth() + 1).padStart(2, '0')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Rapport PDF telecharge');
    } catch (err) {
      toast.error('Erreur lors de la generation du rapport');
    } finally {
      setDownloadingReport(false);
    }
  };

  const handleSendReport = async () => {
    setSendingReport(true);
    try {
      const res = await sendMonthlyReport();
      toast.success(res.data.message);
    } catch (err) {
      const msg = err.response?.data?.detail || 'Erreur lors de l\'envoi';
      toast.error(msg);
    } finally {
      setSendingReport(false);
    }
  };

  const handleToggleJob = async (jobId, currentEnabled) => {
    setTogglingJob(jobId);
    try {
      await updateSchedulerConfig({ [jobId]: { enabled: !currentEnabled } });
      toast.success(!currentEnabled ? 'Planification activee' : 'Planification desactivee');
      fetchScheduler();
    } catch (err) {
      toast.error('Erreur lors de la mise a jour');
    } finally {
      setTogglingJob(null);
    }
  };

  const formatNextRun = (isoStr) => {
    if (!isoStr) return 'Non planifie';
    const d = new Date(isoStr);
    const day = String(d.getDate()).padStart(2, '0');
    const month = String(d.getMonth() + 1).padStart(2, '0');
    const year = d.getFullYear();
    const hours = String(d.getHours()).padStart(2, '0');
    const mins = String(d.getMinutes()).padStart(2, '0');
    return `${day}/${month}/${year} a ${hours}:${mins} UTC`;
  };

  const formatCron = (cron) => {
    if (!cron) return '';
    if (cron.day) return `Le ${cron.day}er du mois a ${cron.hour}h${String(cron.minute || '0').padStart(2, '0')} UTC`;
    return `Chaque jour a ${cron.hour}h${String(cron.minute || '0').padStart(2, '0')} UTC`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]" data-testid="reporting-loading">
        <Loader2 className="h-8 w-8 animate-spin text-[#2D5A4C]" />
      </div>
    );
  }

  const typeLabels = {
    'accompagnement': 'Accompagnement',
    'adhesion_ftpe': 'Adhesion FTPE',
    'dispositif_ess': 'Dispositif ESS',
    'formation': 'Formation',
    'subvention': 'Subvention',
    'diagnostic': 'Diagnostic',
    'incubation': 'Incubation',
  };

  return (
    <div className="space-y-6 animate-fade-in" data-testid="reporting-page">
      {/* Hero header — UX transversale v2 */}
      <PageHero
        title="Reporting"
        subtitle="Indicateurs consolidés et analyses transverses"
        icon={<ChartLine size={24} weight="duotone" className="text-white" />}
        gradient="from-[#00AEEF] to-[#2D5A4C]"
        accents={['#80C51F', '#D5007D']}
      />

      {/* KPI Cards */}
      {kpi && (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-4" data-testid="kpi-grid">
          <KpiCard icon={Folder} label="Dossiers actifs" value={kpi.dossiers.actifs} sub={`${kpi.dossiers.total} total / ${kpi.dossiers.clotures} clotures`} color="#2D5A4C" />
          <KpiCard icon={Buildings} label="Organisations" value={kpi.organisations.total} sub={`${kpi.organisations.membres_ftpe} membres FTPE`} color="#8DB0A1" />
          <KpiCard icon={Target} label="Taux conversion" value={`${kpi.opportunites.taux}%`} sub={`${kpi.opportunites.converties}/${kpi.opportunites.total} opportunites`} color="#D47A62" />
          <KpiCard icon={CurrencyDollar} label="Recouvrement" value={`${kpi.finances.taux_recouvrement}%`} sub={`${kpi.finances.total_recu.toLocaleString('fr-FR')} / ${kpi.finances.total_attendu.toLocaleString('fr-FR')} EUR`} color="#E8A365" />
          <KpiCard icon={Handshake} label="Partenaires" value={kpi.partenaires.actifs} sub={`${kpi.partenaires.total} total`} color="#62A8D4" />
        </div>
      )}

      {/* Charts Row 1 */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Evolution dossiers */}
        <Card className="border-[#E8E6E1] lg:col-span-2" data-testid="chart-evolution">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-[#1A2421]">Evolution des dossiers</CardTitle>
              <Select value={months} onValueChange={setMonths}>
                <SelectTrigger className="w-[130px] h-8 text-xs border-[#E8E6E1]" data-testid="months-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="6">6 derniers mois</SelectItem>
                  <SelectItem value="12">12 derniers mois</SelectItem>
                  <SelectItem value="24">24 derniers mois</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <AreaChart data={evolution}>
                <defs>
                  <linearGradient id="colorDossiers" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#2D5A4C" stopOpacity={0.2} />
                    <stop offset="95%" stopColor="#2D5A4C" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="label" tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" name="Dossiers" stroke="#2D5A4C" fill="url(#colorDossiers)" strokeWidth={2} dot={{ r: 4, fill: '#2D5A4C' }} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Repartition par type */}
        <Card className="border-[#E8E6E1]" data-testid="chart-types">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421]">Repartition par type</CardTitle>
          </CardHeader>
          <CardContent>
            {dossierTypes.length > 0 ? (
              <>
                <ResponsiveContainer width="100%" height={200}>
                  <PieChart>
                    <Pie
                      data={dossierTypes}
                      cx="50%"
                      cy="50%"
                      innerRadius={50}
                      outerRadius={80}
                      paddingAngle={3}
                      dataKey="count"
                      nameKey="type"
                    >
                      {dossierTypes.map((_, i) => (
                        <Cell key={i} fill={COLORS[i % COLORS.length]} />
                      ))}
                    </Pie>
                    <Tooltip content={<CustomTooltip />} />
                  </PieChart>
                </ResponsiveContainer>
                <div className="space-y-1.5 mt-2">
                  {dossierTypes.map((d, i) => (
                    <div key={d.type} className="flex items-center justify-between text-xs">
                      <div className="flex items-center gap-2">
                        <span className="w-2.5 h-2.5 rounded-full" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
                        <span className="text-[#5E6D66]">{typeLabels[d.type] || d.type}</span>
                      </div>
                      <span className="font-semibold text-[#1A2421]">{d.count}</span>
                    </div>
                  ))}
                </div>
              </>
            ) : (
              <p className="text-sm text-[#5E6D66] text-center py-8">Aucun dossier</p>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Charts Row 2 */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Contributions par territoire */}
        <Card className="border-[#E8E6E1]" data-testid="chart-contributions">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421]">Contributions par territoire</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={contributions} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" horizontal={false} />
                <XAxis type="number" tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false}
                  tickFormatter={(v) => v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v}
                />
                <YAxis type="category" dataKey="territoire" tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} width={90} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="montant" name="Montant (EUR)" radius={[0, 4, 4, 0]} maxBarSize={28}>
                  {contributions.map((entry) => (
                    <Cell key={entry.territoire} fill={TERRITORY_COLORS[entry.territoire] || '#5E6D66'} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Pipeline funnel */}
        <Card className="border-[#E8E6E1]" data-testid="chart-pipeline">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421]">Pipeline opportunites</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={pipeline}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E8E6E1" />
                <XAxis dataKey="label" tick={{ fontSize: 10, fill: '#5E6D66' }} axisLine={false} tickLine={false} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11, fill: '#5E6D66' }} axisLine={false} tickLine={false} allowDecimals={false} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="count" name="Opportunites" radius={[4, 4, 0, 0]} maxBarSize={40}>
                  {pipeline.map((_, i) => (
                    <Cell key={i} fill={COLORS[i % COLORS.length]} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Checklist completude + Exports */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Checklist global */}
        <Card className="border-[#E8E6E1]" data-testid="chart-checklist">
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base font-semibold text-[#1A2421]">Completude documentaire</CardTitle>
              {checklist && (
                <Badge variant="outline" className="border-[#2D5A4C] text-[#2D5A4C]">
                  {checklist.percent}% global
                </Badge>
              )}
            </div>
          </CardHeader>
          <CardContent>
            {checklist && checklist.by_type.length > 0 ? (
              <div className="space-y-4">
                {/* Global progress bar */}
                <div>
                  <div className="flex justify-between text-xs text-[#5E6D66] mb-1.5">
                    <span>{checklist.checked_items} / {checklist.total_items} documents valides</span>
                    <span className="font-semibold text-[#1A2421]">{checklist.percent}%</span>
                  </div>
                  <div className="w-full h-3 bg-[#F0EFEB] rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${checklist.percent}%`, backgroundColor: '#2D5A4C' }}
                    />
                  </div>
                </div>

                {/* By type */}
                <div className="space-y-3 pt-2">
                  {checklist.by_type.map((item) => (
                    <div key={item.type}>
                      <div className="flex justify-between text-xs mb-1">
                        <span className="text-[#5E6D66]">{typeLabels[item.type] || item.type}</span>
                        <span className="text-[#1A2421] font-medium">{item.checked}/{item.total} ({item.percent}%)</span>
                      </div>
                      <div className="w-full h-2 bg-[#F0EFEB] rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{
                            width: `${item.percent}%`,
                            backgroundColor: item.percent >= 80 ? '#2D5A4C' : item.percent >= 50 ? '#E8A365' : '#D47A62'
                          }}
                        />
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            ) : (
              <div className="text-center py-8">
                <CheckSquare size={32} className="mx-auto text-[#E8E6E1] mb-2" />
                <p className="text-sm text-[#5E6D66]">Aucune checklist active</p>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Export section */}
        <Card className="border-[#E8E6E1]" data-testid="export-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-base font-semibold text-[#1A2421]">Exports CSV</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-xs text-[#5E6D66] mb-4">
              Telecharger les donnees au format CSV pour analyse externe.
            </p>
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'dossiers', label: 'Dossiers', icon: Folder, color: '#2D5A4C' },
                { key: 'payments', label: 'Paiements', icon: CurrencyDollar, color: '#E8A365' },
                { key: 'organizations', label: 'Organisations', icon: Buildings, color: '#8DB0A1' },
                { key: 'contacts', label: 'Contacts', icon: FileText, color: '#62A8D4' },
              ].map(({ key, label, icon: Icon, color }) => (
                <button
                  key={key}
                  onClick={() => handleExport(key)}
                  disabled={exporting === key}
                  data-testid={`export-${key}`}
                  className="flex items-center gap-3 p-3.5 rounded-lg border border-[#E8E6E1] hover:border-[#2D5A4C] hover:bg-[#F9F8F5] transition-all text-left group"
                >
                  <div className="w-9 h-9 rounded-lg flex items-center justify-center" style={{ backgroundColor: `${color}15` }}>
                    {exporting === key ? (
                      <Loader2 className="h-4 w-4 animate-spin" style={{ color }} />
                    ) : (
                      <Icon size={18} style={{ color }} weight="duotone" />
                    )}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-[#1A2421]">{label}</p>
                    <p className="text-[10px] text-[#5E6D66] flex items-center gap-1">
                      <DownloadSimple size={10} />
                      CSV
                    </p>
                  </div>
                </button>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Rapport mensuel */}
      <Card className="border-[#E8E6E1]" data-testid="monthly-report-section">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-lg bg-[#2D5A4C]/10 flex items-center justify-center">
                <FilePdf size={22} className="text-[#2D5A4C]" weight="duotone" />
              </div>
              <div>
                <CardTitle className="text-base font-semibold text-[#1A2421]">Rapport mensuel PDF</CardTitle>
                <p className="text-xs text-[#5E6D66] mt-0.5">Generez et envoyez un rapport complet avec les KPIs du mois aux administrateurs</p>
              </div>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 pt-1">
            <Button
              variant="outline"
              onClick={handlePreviewReport}
              disabled={downloadingReport}
              className="border-[#E8E6E1] hover:border-[#2D5A4C] hover:bg-[#F9F8F5]"
              data-testid="preview-monthly-report"
            >
              {downloadingReport ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <DownloadSimple size={16} className="mr-2" />
              )}
              Apercu PDF
            </Button>
            <Button
              onClick={handleSendReport}
              disabled={sendingReport}
              className="bg-[#2D5A4C] hover:bg-[#1E4136] text-white"
              data-testid="send-monthly-report"
            >
              {sendingReport ? (
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
              ) : (
                <EnvelopeSimple size={16} className="mr-2" weight="bold" />
              )}
              Envoyer aux administrateurs
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Planification automatique */}
      <Card className="border-[#E8E6E1]" data-testid="scheduler-section">
        <CardHeader className="pb-2">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-[#8DB0A1]/15 flex items-center justify-center">
              <Timer size={22} className="text-[#8DB0A1]" weight="duotone" />
            </div>
            <div>
              <CardTitle className="text-base font-semibold text-[#1A2421]">Planification automatique</CardTitle>
              <p className="text-xs text-[#5E6D66] mt-0.5">Jobs planifies executant automatiquement les taches recurrentes</p>
            </div>
            {schedulerStatus && (
              <Badge variant="outline" className={`ml-auto ${schedulerStatus.running ? 'border-emerald-500 text-emerald-600' : 'border-red-400 text-red-500'}`} data-testid="scheduler-badge">
                {schedulerStatus.running ? 'Actif' : 'Arrete'}
              </Badge>
            )}
          </div>
        </CardHeader>
        <CardContent>
          {schedulerStatus ? (
            <div className="space-y-4 pt-1">
              {schedulerStatus.jobs.map((job) => (
                <div
                  key={job.id}
                  className="flex items-center justify-between p-4 rounded-lg border border-[#E8E6E1] hover:border-[#8DB0A1]/40 transition-colors"
                  data-testid={`scheduler-job-${job.id}`}
                >
                  <div className="flex items-center gap-3 flex-1 min-w-0">
                    <div className={`w-8 h-8 rounded-lg flex items-center justify-center ${job.enabled ? 'bg-[#2D5A4C]/10' : 'bg-[#E8E6E1]'}`}>
                      {job.id === 'rapport_mensuel' ? (
                        <FilePdf size={16} className={job.enabled ? 'text-[#2D5A4C]' : 'text-[#5E6D66]'} weight="duotone" />
                      ) : (
                        <ClockCounterClockwise size={16} className={job.enabled ? 'text-[#2D5A4C]' : 'text-[#5E6D66]'} weight="duotone" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-[#1A2421]">{job.description}</p>
                      <p className="text-[11px] text-[#5E6D66] mt-0.5">{formatCron(job.cron)}</p>
                      <div className="flex items-center gap-3 mt-1">
                        {job.enabled && job.next_run && (
                          <span className="text-[10px] text-[#8DB0A1] flex items-center gap-1">
                            <Play size={9} weight="fill" />
                            Prochaine: {formatNextRun(job.next_run)}
                          </span>
                        )}
                        {job.last_run && (
                          <span className={`text-[10px] flex items-center gap-1 ${job.last_success ? 'text-emerald-500' : 'text-red-400'}`}>
                            {job.last_success ? <CheckSquare size={9} weight="fill" /> : <Pause size={9} weight="fill" />}
                            Dernier: {job.last_detail}
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                  <Switch
                    checked={job.enabled}
                    onCheckedChange={() => handleToggleJob(job.id, job.enabled)}
                    disabled={togglingJob === job.id}
                    data-testid={`toggle-${job.id}`}
                  />
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-6">
              <Loader2 className="h-5 w-5 animate-spin text-[#8DB0A1] mx-auto" />
              <p className="text-xs text-[#5E6D66] mt-2">Chargement...</p>
            </div>
          )}
        </CardContent>
      </Card>
      <ReportingExtras />
    </div>
  );
}
